import * as React from 'react';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Button, Heading, Paragraph } from '@/components/atoms';
import EmptyNote from '@/assets/svg/empty-note.svg?react';
import { icons } from 'lucide-react';

const stateVariants = cva('flex justify-center', {
  variants: {
    align: {
      left: 'items-start text-left',
      center: 'items-center text-center',
    },
  },
  defaultVariants: {
    align: 'center',
  },
});

interface StateProps
  extends React.ComponentProps<'div'>,
    VariantProps<typeof stateVariants> {
  heading?: string;
  description?: string;
  Icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
      titleId?: string;
      desc?: string;
      descId?: string;
    }
  >;
  button?: {
    text: string;
    onClick: () => void;
    icon?: keyof typeof icons;
  };
}

function State({ align, heading, description, Icon, button }: StateProps) {
  const ButtonIcon = button?.icon ? icons[button?.icon] : null;
  const IconComponent = Icon ?? EmptyNote;

  return (
    <div
      data-slot="state"
      data-testid="state"
      className={cn('flex flex-col gap-6', stateVariants({ align }))}
    >
      <IconComponent className="drop-shadow-xs" />
      <div className={cn('flex flex-col gap-1', stateVariants({ align }))}>
        {heading && <Heading size="lg">{heading}</Heading>}
        {description && <Paragraph size="sm">{description}</Paragraph>}
      </div>
      {button && (
        <Button onClick={button.onClick}>
          {ButtonIcon && <ButtonIcon />}
          {button.text}
        </Button>
      )}
    </div>
  );
}

export { State };
